import { useState, useEffect } from 'react'
import { getCachedData } from '@/lib/offlineStore'

export type Unit = {
  id: string
  name: string
  active: boolean
  unit_type: { name: string; sort_order: number } | null
}

/** All units (active + inactive), sorted by type order then alpha.
 *  Reads from syncManager's IndexedDB cache — never fetches from Supabase. */
export function useAllUnits(): Unit[] {
  const [units, setUnits] = useState<Unit[]>([])

  useEffect(() => {
    async function load() {
      const cached = await getCachedData('units') as any[]
      const mapped: Unit[] = cached.map(u => ({
        id: u.id,
        name: u.name,
        active: u.active ?? true,
        unit_type: Array.isArray(u.unit_type) ? u.unit_type[0] ?? null : u.unit_type ?? null,
      }))

      setUnits(mapped.sort((a, b) => {
        const aOrder = a.unit_type?.sort_order ?? 99
        const bOrder = b.unit_type?.sort_order ?? 99
        return aOrder !== bOrder ? aOrder - bOrder : a.name.localeCompare(b.name)
      }))
    }

    load()
  }, [])

  return units
}

/** All unit names (active + inactive), sorted by type order then alpha. */
export function useAllUnitNames(): string[] {
  return useAllUnits().map(u => u.name)
}

/** Active units only, sorted by type order then alpha. */
export function useActiveUnits(): Unit[] {
  return useAllUnits().filter(u => u.active)
}

/** Active unit names only, sorted by type order then alpha. */
export function useActiveUnitNames(): string[] {
  return useAllUnits().filter(u => u.active).map(u => u.name)
}
